import type { CheckBoxeType } from '#components/elements/checkBox/CheckBoxGroup.vue';
import { getTaxTypeName } from '#helpers';
import {
  DIMENSION_DEPENDENCY_RULES_PER_DATATABLE_VIEW,
  sortFromCompareArray,
  type DataTableColumn,
  type DataTableDimensionsType,
  type DataTableKpiDisplayNameType,
  type DataTableViewType
} from '@carrefour-gcs/shared';
import type { TOptions } from 'i18next';

export function kpisToCheckboxList<ColumnId>(
  availableKpis: Pick<DataTableColumn, 'name' | 'label' | 'required'>[],
  selectedKpis: DataTableKpiDisplayNameType[],
  columnsOrder: ColumnId[],
  isWithTax: boolean,
  t: (key: string, options?: TOptions) => string,
  previousList?: CheckBoxeType[],
  forceCheckValue?: boolean
) {
  return (
    availableKpis
      .map((kpi) => {
        let checked: boolean;

        if (kpi.required) {
          checked = true;
        } else if (forceCheckValue !== undefined) {
          checked = forceCheckValue;
        } else {
          checked = previousList?.find((item) => item.id === kpi.name)?.checked ?? selectedKpis.includes(kpi.name);
        }

        return {
          id: kpi.name,
          disabled: kpi.required,
          checked,
          label: t(kpi.label, { taxType: t(getTaxTypeName(isWithTax)) })
        };
      })
      .sort((a, b) => sortFromCompareArray(columnsOrder)(a.id as ColumnId, b.id as ColumnId)) ?? []
  );
}

export function applyCheckboxDependencyRules(
  datatableView: DataTableViewType,
  dimensionCheckboxes: CheckBoxeType[]
): CheckBoxeType[] {
  const exclusionRules = DIMENSION_DEPENDENCY_RULES_PER_DATATABLE_VIEW[datatableView];

  if (!exclusionRules) {
    return dimensionCheckboxes;
  }

  const disabledDimensions = new Set<string>();

  for (const { id, checked } of dimensionCheckboxes) {
    if (!checked) {
      continue;
    }

    const excludes = exclusionRules[id as DataTableDimensionsType]?.excludes;
    if (excludes) {
      excludes.forEach((excludedId) => disabledDimensions.add(excludedId));
    }
  }

  return dimensionCheckboxes.map((item) => ({
    ...item,
    disabled: disabledDimensions.has(item.id)
  }));
}

export function dimensionsToCheckboxList(
  availableDimensions: Pick<DataTableColumn, 'name' | 'label' | 'required'>[],
  selectedDimensions: DataTableDimensionsType[],
  disabledDimensions: DataTableDimensionsType[],
  dimensionsOrder: DataTableDimensionsType[],
  t: (key: string) => string,
  previousList?: CheckBoxeType[]
) {
  return (
    availableDimensions
      .map((dimension) => ({
        id: dimension.name,
        disabled: dimension.required || disabledDimensions.includes(dimension.name),
        checked:
          previousList?.find((item) => item.id === dimension.name)?.checked ??
          selectedDimensions.includes(dimension.name),
        label: t(dimension.label)
      }))
      .sort((a, b) =>
        sortFromCompareArray(dimensionsOrder)(a.id as DataTableDimensionsType, b.id as DataTableDimensionsType)
      ) ?? []
  );
}
